import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdditionalOption } from '../types';

interface OptionPricingDisplayProps {
  option: AdditionalOption;
  onAddPricing: (optionId: string, isCore: boolean, optionType: string) => void;
  onRemovePricing: (optionId: string, isCore: boolean, pricingIndex: number) => void;
  renderPricingPreview: (pricing: any) => string | null;
}

export function OptionPricingDisplay({
  option,
  onAddPricing,
  onRemovePricing,
  renderPricingPreview
}: OptionPricingDisplayProps) {
  const hasPricing = option.pricing && option.pricing.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Option Pricing</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAddPricing(option.id, false, option.type)}
          className="h-8 px-3 text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Pricing
        </Button>
      </div>

      <AnimatePresence>
        {hasPricing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            {option.pricing!.map((pricing, pricingIdx) => (
              <motion.div
                key={pricingIdx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">
                    {renderPricingPreview(pricing)}
                  </Badge>
                  {pricing.description && (
                    <span className="text-xs text-slate-600">{pricing.description}</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemovePricing(option.id, false, pricingIdx)}
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {!hasPricing && (
        <div className="text-xs text-slate-500 italic">
          No pricing configured for this option
        </div>
      )}
    </div>
  );
}
